// client/src/coms/views/canvas_parts/CanvasPaintSettingsPanel.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  cleanPaintBrushLoad,
  getPaintBrushLoadInfo,
  getPaintSettings,
  normalisePaintSettings,
  setPaintBrushLoading,
  setPaintDebugViewMode as setGlobalPaintDebugViewMode,
  setPaintSettings as setGlobalPaintSettings,
} from './canvas_room_view_parts/paintBrushUtils';
import PhysicalPaintSection from '../../canvas/paint-engine/integration/PhysicalPaintSection';
import { getReadyPaintDevice } from '../../canvas/paint-engine/integration/physicalPaintFlag';

const TOOL_OPTIONS = [
  { id: 'tube', label: 'Tube' },
  { id: 'knife', label: 'Palette Knife' },
  { id: 'brush', label: 'Brush' },
];

const VIEW_OPTIONS = [
  { id: 'final', label: 'Final image' },
  { id: 'color', label: 'Colour' },
  { id: 'heightmap', label: 'Heightmap' },
];

function clampNumber(value, min, max, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
}

function normaliseHexColor(value, fallback = '#d52b24') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

function SelectRow({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: '100%',
          minHeight: 34,
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(0,0,0,0.24)',
          color: 'rgba(255,255,255,0.92)',
          padding: '0 10px',
          fontSize: 12,
          fontWeight: 850,
          outline: 'none',
        }}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function SliderRow({ label, value, min, max, step = 1, suffix = '', onChange }) {
  const safeValue = clampNumber(value, min, max, min);
  return (
    <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, fontWeight: 850, color: 'rgba(255,255,255,0.54)', fontVariantNumeric: 'tabular-nums' }}>
          {safeValue}{suffix}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{ width: '100%', height: 18, margin: 0, accentColor: 'rgba(255,255,255,0.92)' }}
      />
    </label>
  );
}

function PaintButton({ children, onClick, onPointerDown, onPointerUp }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{
        minHeight: 32,
        borderRadius: 9,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.065)',
        color: 'rgba(255,255,255,0.88)',
        fontSize: 11,
        fontWeight: 850,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export default function CanvasPaintSettingsPanel({
  strokeColor = '#d52b24',
  setStrokeColor,
  brushSize = 70,
  setBrushSize,
  paintSettings: externalPaintSettings,
  onPaintSettingsChange,
  paintDebugViewMode = 'final',
  setPaintDebugViewMode,
  onDryPaint,
  onClearPaint,
}) {
  const initial = useMemo(() => normalisePaintSettings({
    ...getPaintSettings(),
    ...(externalPaintSettings || {}),
    paintColor: externalPaintSettings?.paintColor || strokeColor,
    toolSize: externalPaintSettings?.toolSize || brushSize,
    view: externalPaintSettings?.view || paintDebugViewMode,
  }), []);
  const [tool, setTool] = useState(initial.tool);
  const [paintColor, setPaintColor] = useState(initial.paintColor);
  const [toolSize, setToolSize] = useState(initial.toolSize);
  const [bristleCount, setBristleCount] = useState(initial.bristleCount);
  const [bristleThickness, setBristleThickness] = useState(initial.bristleThickness);
  const [bristleLength, setBristleLength] = useState(initial.bristleLength);
  const [brushPressure, setBrushPressure] = useState(initial.brushPressure);
  const [infinitePaint, setInfinitePaint] = useState(initial.infinitePaint);
  const [sideDeposits, setSideDeposits] = useState(initial.sideDeposits !== false);
  const [liquid, setLiquid] = useState(initial.liquid);
  const [stickiness, setStickiness] = useState(initial.stickiness);
  const [gloss, setGloss] = useState(initial.gloss);
  const [lightAngle, setLightAngle] = useState(initial.lightAngle);
  const [lightElevation, setLightElevation] = useState(initial.lightElevation);
  const [view, setView] = useState(initial.view);
  const [physical, setPhysical] = useState(initial.physical);
  // The physical section appears only when the physical engine is running.
  const physicalActive = Boolean(getReadyPaintDevice());
  const [loadLabel, setLoadLabel] = useState('Hold to fill brush');

  const settings = useMemo(() => normalisePaintSettings({
    tool,
    view,
    paintColor,
    toolSize,
    bristleCount,
    bristleThickness,
    bristleLength,
    brushPressure,
    infinitePaint,
    sideDeposits,
    liquid,
    stickiness,
    gloss,
    lightAngle,
    lightElevation,
    physical,
  }), [physical, brushPressure, bristleCount, bristleLength, bristleThickness, gloss, infinitePaint, sideDeposits, lightAngle, lightElevation, liquid, paintColor, stickiness, tool, toolSize, view]);

  useEffect(() => {
    const next = setGlobalPaintSettings(settings);
    if (typeof onPaintSettingsChange === 'function') onPaintSettingsChange(next);
  }, [onPaintSettingsChange, settings]);

  useEffect(() => {
    const next = normaliseHexColor(strokeColor, paintColor);
    if (next !== paintColor) setPaintColor(next);
  }, [strokeColor]);

  useEffect(() => {
    const next = Math.round(clampNumber(brushSize, 4, 220, toolSize));
    if (next !== toolSize) setToolSize(next);
  }, [brushSize]);

  useEffect(() => {
    const next = VIEW_OPTIONS.some((option) => option.id === paintDebugViewMode) ? paintDebugViewMode : 'final';
    if (next !== view) setView(next);
  }, [paintDebugViewMode]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const brushInfo = getPaintBrushLoadInfo();
      setLoadLabel(brushInfo.loading
        ? `Filling brush... ${Math.round(brushInfo.percent)}%`
        : brushInfo.percent > 0
          ? `Brush load ${Math.round(brushInfo.percent)}%`
          : 'Hold to fill brush');
    }, 100);
    return () => window.clearInterval(interval);
  }, []);

  function changePaintColor(value) {
    const next = normaliseHexColor(value, paintColor);
    setPaintColor(next);
    if (typeof setStrokeColor === 'function') setStrokeColor(next);
  }

  function changeToolSize(value) {
    const next = Math.round(clampNumber(value, 4, 220, toolSize));
    setToolSize(next);
    if (typeof setBrushSize === 'function') setBrushSize(next);
  }

  function changeView(value) {
    const next = VIEW_OPTIONS.some((option) => option.id === value) ? value : 'final';
    setView(next);
    setGlobalPaintDebugViewMode(next);
    if (typeof setPaintDebugViewMode === 'function') setPaintDebugViewMode(next);
  }

  return (
    <div style={{ display: 'grid', gap: 11, minWidth: 0 }}>
      <div style={{ display: 'grid', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span style={{ fontSize: 14, fontWeight: 950, color: 'rgba(255,255,255,0.94)' }}>{physicalActive ? 'Physical Paint' : 'Oil Paint'}</span>
          <span style={{ fontSize: 9, fontWeight: 900, color: '#8fd3ff' }}>{physicalActive ? 'WebGPU paint engine' : 'GPU physical-brush-v1'}</span>
        </div>
        <span style={{ fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,0.5)' }}>
          {physicalActive
            ? 'Watercolour to impasto oil: pigments mix, water flows and dries, bristles pick up and smear wet paint, and the paint surface is lit.'
            : 'Colour, 4000-unit height and live GPU lighting. Paint can be extruded, brushed, loaded and dried.'}
        </span>
      </div>

      <SelectRow label="Paint tool" value={tool} options={TOOL_OPTIONS} onChange={setTool} />

      <label style={{ display: 'grid', gap: 5 }}>
        <span style={{ fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>
          Paint colour
        </span>
        <input
          type="color"
          value={paintColor}
          onChange={(event) => changePaintColor(event.target.value)}
          style={{ width: '100%', height: 34, padding: 2, borderRadius: 9, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.22)' }}
        />
      </label>

      <SliderRow label="Tool width" value={toolSize} min={4} max={220} suffix=" px" onChange={changeToolSize} />
      {physicalActive ? (
        <PhysicalPaintSection
          value={physical}
          onChange={setPhysical}
          SelectRow={SelectRow}
          SliderRow={SliderRow}
          previewSettings={{ tool, paintColor, toolSize, brushPressure, lightAngle, lightElevation, gloss }}
        />
      ) : null}

      {physicalActive ? (
        <SliderRow label="Brush pressure" value={brushPressure} min={1} max={100} suffix="%" onChange={setBrushPressure} />
      ) : null}

      {/* The controls below drive the original oil renderer only. */}
      {tool === 'brush' && !physicalActive ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <SliderRow label="Bristle count" value={bristleCount} min={1} max={250} onChange={setBristleCount} />
          <SliderRow label="Bristle thickness" value={bristleThickness} min={1} max={100} suffix="%" onChange={setBristleThickness} />
          <SliderRow label="Bristle length" value={bristleLength} min={1} max={300} suffix=" px" onChange={setBristleLength} />
          <SliderRow label="Brush pressure" value={brushPressure} min={1} max={100} suffix="%" onChange={setBrushPressure} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.8)' }}>
            <input type="checkbox" checked={infinitePaint} onChange={(event) => setInfinitePaint(event.target.checked)} />
            Infinite paint
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.8)' }}>
            <input type="checkbox" checked={sideDeposits} onChange={(event) => setSideDeposits(event.target.checked)} />
            Side deposits
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
            <PaintButton onPointerDown={() => setPaintBrushLoading(true)} onPointerUp={() => setPaintBrushLoading(false)}>{loadLabel}</PaintButton>
            <PaintButton onClick={() => cleanPaintBrushLoad()}>Clean brush</PaintButton>
          </div>
        </div>
      ) : null}

      {physicalActive ? null : (
        <>
          <SliderRow label="Liquid" value={liquid} min={0} max={100} suffix="%" onChange={setLiquid} />
          <SliderRow label="Cohesion" value={stickiness} min={0} max={100} suffix="%" onChange={setStickiness} />
        </>
      )}
      <SliderRow label="Gloss" value={gloss} min={0} max={100} suffix="%" onChange={setGloss} />
      <SliderRow label="Light angle" value={lightAngle} min={0} max={360} suffix="°" onChange={setLightAngle} />
      <SliderRow label="Light elevation" value={lightElevation} min={1} max={89} suffix="°" onChange={setLightElevation} />
      <div style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>
          Layer view
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => changeView(option.id)}
              style={{
                minHeight: 31,
                borderRadius: 8,
                border: view === option.id ? '1px solid rgba(143,211,255,0.72)' : '1px solid rgba(255,255,255,0.12)',
                background: view === option.id ? 'rgba(143,211,255,0.16)' : 'rgba(255,255,255,0.055)',
                color: view === option.id ? '#bfe8ff' : 'rgba(255,255,255,0.76)',
                fontSize: 10,
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              {option.id === 'heightmap' ? 'Height' : option.label.replace(' image', '')}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
        <PaintButton onClick={onDryPaint}>Dry current paint</PaintButton>
        <PaintButton onClick={onClearPaint}>Clear paint</PaintButton>
      </div>

      <div style={{ padding: 9, borderRadius: 9, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', fontSize: 10, lineHeight: 1.5, color: 'rgba(255,255,255,0.55)' }}>
        Tube height = width ÷ 2. A 100 px tube is 50 height units. Heightmap range is 0–4000.
      </div>
    </div>
  );
}
